import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import API_BASE_URL from "../../config";
import { clearAuthSession, getStoredToken } from "../../utils/authStorage";

const WARNING_SECONDS = 60;

const PUBLIC_PATHS = new Set([
  "/",
  "/login",
  "/verify-login",
  "/forgot-password",
  "/reset-password",
  "/activate-account"
]);

function SessionTimeoutManager() {
  const navigate = useNavigate();
  const location = useLocation();

  const [timeoutMinutes, setTimeoutMinutes] = useState(60);
  const [warningOpen, setWarningOpen] = useState(false);
  const [countdown, setCountdown] = useState(WARNING_SECONDS);

  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());

  const pathname = location.pathname;
  const token = getStoredToken();
  const demoSession = localStorage.getItem("demoSession");
  const isPublicPath =
    PUBLIC_PATHS.has(pathname) ||
    pathname.startsWith("/auth/microsoft/callback") ||
    pathname.startsWith("/soa/verify/") ||
    pathname.startsWith("/aca-consent/");

  const isActiveProtectedSession = Boolean(token) && !isPublicPath && !demoSession;

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const forceLogout = useCallback(() => {
    clearTimers();
    setWarningOpen(false);
    setCountdown(WARNING_SECONDS);
    clearAuthSession();
    navigate("/login", { replace: true });
  }, [clearTimers, navigate]);

  const beginCountdown = useCallback(() => {
    setWarningOpen(true);
    setCountdown(WARNING_SECONDS);

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
          return 0;
        }

        return prev - 1;
      });
    }, 1000);
  }, []);

  const scheduleTimers = useCallback(() => {
    clearTimers();

    if (!isActiveProtectedSession) {
      setWarningOpen(false);
      setCountdown(WARNING_SECONDS);
      return;
    }

    const totalMs = Number(timeoutMinutes) * 60 * 1000;
    const warningMs = Math.max(totalMs - WARNING_SECONDS * 1000, 1000);

    warningTimerRef.current = setTimeout(() => {
      beginCountdown();
    }, warningMs);

    logoutTimerRef.current = setTimeout(() => {
      forceLogout();
    }, totalMs);
  }, [
    beginCountdown,
    clearTimers,
    forceLogout,
    isActiveProtectedSession,
    timeoutMinutes
  ]);

  const resetSessionTimer = useCallback(() => {
    if (!isActiveProtectedSession) return;

    lastActivityAtRef.current = Date.now();

    if (warningOpen) {
      setWarningOpen(false);
      setCountdown(WARNING_SECONDS);
    }

    scheduleTimers();
  }, [isActiveProtectedSession, scheduleTimers, warningOpen]);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/platform-settings`);
        const data = await res.json();

        if (!isMounted) return;

        const minutes = Number(data?.session_inactivity_timeout_minutes);
        setTimeoutMinutes([10, 30, 60, 120].includes(minutes) ? minutes : 60);
      } catch (err) {
        console.error("Load session timeout settings error:", err.message);
        if (isMounted) {
          setTimeoutMinutes(60);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    scheduleTimers();
    return () => clearTimers();
  }, [scheduleTimers, clearTimers]);

  useEffect(() => {
    if (!isActiveProtectedSession) return;

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

    const handleActivity = () => {
      resetSessionTimer();
    };

    events.forEach((eventName) =>
      window.addEventListener(eventName, handleActivity, { passive: true })
    );

    return () => {
      events.forEach((eventName) =>
        window.removeEventListener(eventName, handleActivity)
      );
    };
  }, [isActiveProtectedSession, resetSessionTimer]);

  if (!warningOpen || !isActiveProtectedSession) return null;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 6000,
        padding: "20px"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "440px",
          background: "#ffffff",
          borderRadius: "20px",
          boxShadow: "0 24px 80px rgba(15, 23, 42, 0.24)",
          padding: "28px"
        }}
      >
        <h2
          style={{
            margin: "0 0 10px 0",
            fontSize: "24px",
            fontWeight: 700,
            color: "#0f172a"
          }}
        >
          Session Timeout Warning
        </h2>

        <p
          style={{
            margin: "0 0 16px 0",
            color: "#475569",
            lineHeight: 1.6
          }}
        >
          You have been inactive and will be logged out automatically in{" "}
          <strong>{countdown}</strong> second{countdown === 1 ? "" : "s"}.
        </p>

        <p
          style={{
            margin: "0 0 22px 0",
            color: "#64748b",
            lineHeight: 1.6
          }}
        >
          Select <strong>Stay Logged In</strong> to continue your session.
        </p>

        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "flex-end",
            flexWrap: "wrap"
          }}
        >
          <button
            type="button"
            className="sales-secondary-btn"
            onClick={forceLogout}
          >
            Logout Now
          </button>

          <button
            type="button"
            className="sales-primary-btn"
            onClick={resetSessionTimer}
          >
            Stay Logged In
          </button>
        </div>
      </div>
    </div>
  );
}

export default SessionTimeoutManager;